/** @module @liquidefy/code-editor/terminal/terminal-collaboration @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  TerminalCollabState,
  ShareSessionId,
  ShareParticipant,
  SharePermissions,
  ShareConfig,
  ParticipantId,
  ParticipantRole,
  ChatMessage,
  TerminalCursorState,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asParticipantId,
  asShareSessionId,
  now,
  roleToPermissions as engineRoleToPermissions,
  isShareExpired as engineIsShareExpired,
  DEFAULT_SHARE_CONFIG,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 SHARE LIFECYCLE — createShareSession, endShareSession, isShareActive, isShareExpired
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new terminal collaboration/sharing session.
 */
export function createShareSession(
  shareSessionId: string,
  hostName: string
): EditorResult<TerminalCollabState> {
  const hostId = asParticipantId(generateId('participant'));
  const host: ShareParticipant = {
    id: hostId,
    name: hostName,
    role: 'owner',
    status: 'connected',
    joinedAt: now(),
  };

  const state: TerminalCollabState = {
    shareSessionId: asShareSessionId(shareSessionId),
    isHost: true,
    participants: [host],
    permissions: roleToPermissions('owner'),
    chat: [],
    config: DEFAULT_SHARE_CONFIG,
  };

  return ok(state, 'SHARE_CREATED', `Share session created by ${hostName}`);
}

/**
 * End the share session by disconnecting all participants.
 */
export function endShareSession(state: TerminalCollabState): EditorResult<TerminalCollabState> {
  const updated: TerminalCollabState = {
    ...state,
    participants: state.participants.map(p => ({
      ...p,
      status: 'disconnected' as const,
    })),
  };
  return ok(updated, 'SHARE_ENDED', 'Share session ended');
}

/**
 * Check if the share session has any connected participants (besides host).
 */
export function isShareActive(state: TerminalCollabState): boolean {
  return state.participants.some(p => p.status === 'connected');
}

/**
 * Check if the share session has expired based on config.
 */
export function isShareExpired(config: ShareConfig): boolean {
  if (!config.expiresAt) return false;
  return now() > config.expiresAt;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 PARTICIPANT MANAGEMENT — add, remove, update status/role, getters
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a new participant to the share session.
 */
export function addParticipant(
  state: TerminalCollabState,
  name: string,
  role: ParticipantRole
): EditorResult<TerminalCollabState> {
  if (isAtCapacity(state)) {
    return err(
      'SHARE_AT_CAPACITY',
      `Maximum participants (${state.config.maxParticipants}) reached`
    );
  }

  const participantId = asParticipantId(generateId('participant'));
  const participant: ShareParticipant = {
    id: participantId,
    name,
    role,
    status: 'connected',
    joinedAt: now(),
  };

  const updated: TerminalCollabState = {
    ...state,
    participants: [...state.participants, participant],
  };

  return ok(updated, 'PARTICIPANT_ADDED', `${name} joined as ${role}`);
}

/**
 * Remove a participant from the share session.
 */
export function removeParticipant(
  state: TerminalCollabState,
  participantId: ParticipantId
): EditorResult<TerminalCollabState> {
  const exists = state.participants.some(p => p.id === participantId);
  if (!exists) {
    return err('PARTICIPANT_NOT_FOUND', `Participant '${participantId}' not found`);
  }

  const updated: TerminalCollabState = {
    ...state,
    participants: state.participants.filter(p => p.id !== participantId),
  };

  return ok(updated, 'PARTICIPANT_REMOVED', `Participant '${participantId}' removed`);
}

/**
 * Update the connection status of a participant.
 */
export function updateParticipantStatus(
  state: TerminalCollabState,
  participantId: ParticipantId,
  status: ShareParticipant['status']
): EditorResult<TerminalCollabState> {
  const exists = state.participants.some(p => p.id === participantId);
  if (!exists) {
    return err('PARTICIPANT_NOT_FOUND', `Participant '${participantId}' not found`);
  }

  const participants = state.participants.map(p =>
    p.id === participantId ? { ...p, status } : p
  );
  return ok({ ...state, participants }, 'PARTICIPANT_STATUS_UPDATED', `Participant status set to ${status}`);
}

/**
 * Update the role of a participant.
 */
export function updateParticipantRole(
  state: TerminalCollabState,
  participantId: ParticipantId,
  role: ParticipantRole
): EditorResult<TerminalCollabState> {
  const participant = state.participants.find(p => p.id === participantId);
  if (!participant) {
    return err('PARTICIPANT_NOT_FOUND', `Participant '${participantId}' not found`);
  }
  if (participant.role === 'owner') {
    return err('CANNOT_CHANGE_OWNER_ROLE', 'Cannot change the role of the session owner');
  }

  const participants = state.participants.map(p =>
    p.id === participantId ? { ...p, role } : p
  );
  return ok({ ...state, participants }, 'PARTICIPANT_ROLE_UPDATED', `Participant role changed to ${role}`);
}

/**
 * Get a participant by ID.
 */
export function getParticipant(
  state: TerminalCollabState,
  participantId: ParticipantId
): ShareParticipant | undefined {
  return state.participants.find(p => p.id === participantId);
}

/**
 * Get all connected (active) participants.
 */
export function getActiveParticipants(state: TerminalCollabState): ShareParticipant[] {
  return state.participants.filter(p => p.status === 'connected');
}

/**
 * Get the total participant count.
 */
export function participantCount(state: TerminalCollabState): number {
  return state.participants.length;
}

/**
 * Check if the session is at max capacity.
 */
export function isAtCapacity(state: TerminalCollabState): boolean {
  return state.participants.length >= state.config.maxParticipants;
}

/**
 * Get the host (owner) participant.
 */
export function getHost(state: TerminalCollabState): ShareParticipant | undefined {
  return state.participants.find(p => p.role === 'owner');
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 PERMISSIONS — canRead, canWrite, canExecute, canShare, canManage, role/grant/revoke
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Default permissions for each role.
 */
export const ROLE_PERMISSIONS: Record<ParticipantRole, SharePermissions> = {
  owner: { read: true, write: true, execute: true, share: true, manage: true },
  editor: { read: true, write: true, execute: true, share: false, manage: false },
  viewer: { read: true, write: false, execute: false, share: false, manage: false },
  commenter: { read: true, write: false, execute: false, share: false, manage: false },
};

/**
 * Check if a participant can read.
 */
export function canRead(state: TerminalCollabState, participantId: ParticipantId): boolean {
  return hasPermission(getParticipantPermissions(state, participantId), 'read');
}

/**
 * Check if a participant can write.
 */
export function canWrite(state: TerminalCollabState, participantId: ParticipantId): boolean {
  return hasPermission(getParticipantPermissions(state, participantId), 'write');
}

/**
 * Check if a participant can execute commands.
 */
export function canExecute(state: TerminalCollabState, participantId: ParticipantId): boolean {
  return hasPermission(getParticipantPermissions(state, participantId), 'execute');
}

/**
 * Check if a participant can share.
 */
export function canShare(state: TerminalCollabState, participantId: ParticipantId): boolean {
  return hasPermission(getParticipantPermissions(state, participantId), 'share');
}

/**
 * Check if a participant can manage the session.
 */
export function canManage(state: TerminalCollabState, participantId: ParticipantId): boolean {
  return hasPermission(getParticipantPermissions(state, participantId), 'manage');
}

/**
 * Convert a role to its default permissions.
 */
export function roleToPermissions(role: ParticipantRole): SharePermissions {
  return ROLE_PERMISSIONS[role];
}

/**
 * Check if a permissions object grants a specific action.
 */
export function hasPermission(
  permissions: SharePermissions,
  action: keyof SharePermissions
): boolean {
  return permissions[action];
}

/**
 * Grant a specific permission to a participant. Only managers/owners can do this.
 * This modifies the global permissions if the participant is the host.
 */
export function grantPermission(
  state: TerminalCollabState,
  _participantId: ParticipantId,
  action: keyof SharePermissions
): EditorResult<TerminalCollabState> {
  // In the pure state model, we update the session-level permissions
  const updated: TerminalCollabState = {
    ...state,
    permissions: { ...state.permissions, [action]: true },
  };
  return ok(updated, 'PERMISSION_GRANTED', `Permission '${action}' granted`);
}

/**
 * Revoke a specific permission from a participant.
 */
export function revokePermission(
  state: TerminalCollabState,
  _participantId: ParticipantId,
  action: keyof SharePermissions
): EditorResult<TerminalCollabState> {
  const updated: TerminalCollabState = {
    ...state,
    permissions: { ...state.permissions, [action]: false },
  };
  return ok(updated, 'PERMISSION_REVOKED', `Permission '${action}' revoked`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 CHAT — addChatMessage, getChatMessages, deleteChatMessage, count, format
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Add a chat message to the share session.
 */
export function addChatMessage(
  state: TerminalCollabState,
  authorId: ParticipantId,
  text: string
): TerminalCollabState {
  const message: ChatMessage = {
    id: generateId('chat'),
    authorId,
    text,
    timestamp: now(),
  };
  return { ...state, chat: [...state.chat, message] };
}

/**
 * Get chat messages, optionally since a timestamp.
 */
export function getChatMessages(
  state: TerminalCollabState,
  since?: number
): ChatMessage[] {
  if (since === undefined) return state.chat;
  return state.chat.filter(m => m.timestamp >= since);
}

/**
 * Delete a chat message by ID.
 */
export function deleteChatMessage(
  state: TerminalCollabState,
  messageId: string
): TerminalCollabState {
  return { ...state, chat: state.chat.filter(m => m.id !== messageId) };
}

/**
 * Get the total number of chat messages.
 */
export function chatMessageCount(state: TerminalCollabState): number {
  return state.chat.length;
}

/**
 * Format a chat message with the author's name for display.
 */
export function formatChatMessage(
  message: ChatMessage,
  participants: ShareParticipant[]
): string {
  const author = participants.find(p => p.id === message.authorId);
  const name = author?.name ?? 'Unknown';
  const time = new Date(message.timestamp);
  const timeStr = `${time.getHours().toString().padStart(2, '0')}:${time.getMinutes().toString().padStart(2, '0')}`;
  return `[${timeStr}] ${name}: ${message.text}`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 CURSOR SHARING — updateCursor, getCursors, following
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Update a participant's cursor position.
 */
export function updateParticipantCursor(
  state: TerminalCollabState,
  participantId: ParticipantId,
  cursor: TerminalCursorState
): TerminalCollabState {
  const participants = state.participants.map(p =>
    p.id === participantId ? { ...p, cursor } : p
  );
  return { ...state, participants };
}

/**
 * Get all participant cursors (for rendering remote cursors).
 */
export function getParticipantCursors(
  state: TerminalCollabState
): Array<{ participantId: ParticipantId; name: string; cursor: TerminalCursorState }> {
  return state.participants
    .filter(p => p.cursor !== undefined && p.status === 'connected')
    .map(p => ({
      participantId: p.id,
      name: p.name,
      cursor: p.cursor!,
    }));
}

/**
 * Check if a participant is following another participant.
 * Following state is tracked via the chat messages convention (follow:leaderId).
 */
export function isFollowing(
  state: TerminalCollabState,
  participantId: ParticipantId
): boolean {
  return state.chat.some(m =>
    m.authorId === participantId &&
    m.text.startsWith('__follow:') &&
    !state.chat.some(m2 =>
      m2.authorId === participantId &&
      m2.text === '__unfollow' &&
      m2.timestamp > m.timestamp
    )
  );
}

/**
 * Start following another participant.
 */
export function startFollowing(
  state: TerminalCollabState,
  followerId: ParticipantId,
  leaderId: ParticipantId
): TerminalCollabState {
  const message: ChatMessage = {
    id: generateId('follow'),
    authorId: followerId,
    text: `__follow:${leaderId}`,
    timestamp: now(),
  };
  return { ...state, chat: [...state.chat, message] };
}

/**
 * Stop following.
 */
export function stopFollowing(
  state: TerminalCollabState,
  followerId: ParticipantId
): TerminalCollabState {
  const message: ChatMessage = {
    id: generateId('unfollow'),
    authorId: followerId,
    text: '__unfollow',
    timestamp: now(),
  };
  return { ...state, chat: [...state.chat, message] };
}

/**
 * Get all participants currently following a leader.
 */
export function getFollowers(
  state: TerminalCollabState,
  leaderId: ParticipantId
): ParticipantId[] {
  const followers: Set<string> = new Set();

  for (const msg of state.chat) {
    if (msg.text === `__follow:${leaderId}`) {
      followers.add(msg.authorId as string);
    } else if (msg.text === '__unfollow' && followers.has(msg.authorId as string)) {
      followers.delete(msg.authorId as string);
    }
  }

  return Array.from(followers) as ParticipantId[];
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

function getParticipantPermissions(
  state: TerminalCollabState,
  participantId: ParticipantId
): SharePermissions {
  const participant = state.participants.find(p => p.id === participantId);
  if (!participant) {
    return { read: false, write: false, execute: false, share: false, manage: false };
  }
  return ROLE_PERMISSIONS[participant.role];
}
